import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import api from '../../services/api';
import { getMediaUrl } from '../../services/media';
import styles from './News.module.css';

const LIMIT = 24;

const News = () => {
  const { t } = useLanguage();
  const [news, setNews] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const loaderRef = useRef(null);

  const fetchNews = useCallback((currentPage, append = false) => {
    if (currentPage === 1) setInitialLoading(true);
    else setLoading(true);

    api.get('/news', { params: { page: currentPage } })
      .then((res) => {
        const { news: data = [], total = 0, page: p = 1 } = res.data;
        setNews((prev) => append ? [...prev, ...data] : data);
        setHasMore(p * LIMIT < total);
      })
      .catch(() => {
        if (!append) setNews([]);
        setHasMore(false);
      })
      .finally(() => {
        setInitialLoading(false);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    fetchNews(1, false);
  }, [fetchNews]);

  useEffect(() => {
    if (page === 1) return;
    fetchNews(page, true);
  }, [page, fetchNews]);

  useEffect(() => {
    if (!loaderRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading && !initialLoading) {
          setPage((p) => p + 1);
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(loaderRef.current);
    return () => observer.disconnect();
  }, [hasMore, loading, initialLoading]);

  if (initialLoading) return <div className={styles.loading}>{t('common.loading')}</div>;

  return (
    <div className={styles.news}>
      <div className={styles.container}>
        <h1 className={styles.title}>{t('news.title')}</h1>

        {news.length === 0 ? (
          <div className={styles.empty}>Новостей пока нет</div>
        ) : (
          <div className={styles.grid}>
            {news.map((item) => (
              <Link to={`/news/${item.id}`} key={item.id} className={styles.card}>
                <div className={styles.image}>
                  {item.imageUrl && <img src={getMediaUrl(item.imageUrl)} alt={item.title} />}
                </div>
                <div className={styles.content}>
                  <span className={styles.date}>
                    {new Date(item.createdAt).toLocaleDateString('ru-RU')}
                  </span>
                  <h3 className={styles.cardTitle}>{item.title}</h3>
                  <p className={styles.excerpt}>
                    {item.content?.replace(/<[^>]+>/g, '').slice(0, 120)}...
                  </p>
                  <span className={styles.readMore}>{t('news.readMore')}</span>
                </div>
              </Link>
            ))}
          </div>
        )}

        <div ref={loaderRef} className={styles.loaderTrigger}>
          {loading && <div className={styles.loadingMore}>{t('common.loading')}</div>}
          {!hasMore && news.length > 0 && (
            <p className={styles.endMessage}>Все новости загружены</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default News;
